import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  type ILogger,
} from '@microsoft/signalr';

// Same env var apiClient uses (src/shared/api/api-client.ts:15). In local dev
// it's empty, so SignalR's relative URLs resolve against the Vite dev server
// origin and Vite's /api proxy forwards to dev-api.scrips.com. In production
// builds it's the absolute API origin (e.g. https://dev-api.scrips.com), so
// SignalR negotiates against the API host instead of the React origin —
// otherwise nginx in the React pod returns 405 because it only serves
// static files.
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Build a SignalR HubConnection for any Scrips notifications hub.
 *
 * Mirrors Flutter's `signalRCall` helper (scrips_core/utils/signalr_hub.dart):
 *   - withAutomaticReconnect — exponential backoff handled by the lib
 *   - accessTokenFactory     — pulled fresh per (re)connect, so tokens that
 *                              refreshed since last connect are picked up
 *
 * Note on timeouts: Flutter sets `serverTimeoutInMilliseconds: 10_000_000`
 * and `keepAliveIntervalInMilliseconds: 500_000_000` — values so large the
 * Dart lib clamps them silently. The JS @microsoft/signalr lib respects
 * them literally, which means the client never pings the server, the
 * server's 30 s idle timeout kicks in, and the connection drops with
 * "Connection closed with an error". We use sane defaults (60 s server
 * timeout, 15 s keepalive ping) instead.
 *
 * Caller is responsible for `.start()` and `.stop()` lifecycle.
 */

/**
 * Custom logger that filters out the StrictMode-induced "stopped during
 * negotiation" message — that's expected dev-only noise when an effect
 * cleanup races with the in-flight start. Real errors still log.
 */
const QUIET_LOGGER: ILogger = {
  log(level, message) {
    if (
      message.includes('stopped during negotiation') ||
      message.includes('Stopping connection') ||
      message.includes('connection.start() called when the connection is')
    ) {
      return;
    }
    if (level >= LogLevel.Error) console.error(`[SignalR] ${message}`);
    else if (level >= LogLevel.Warning) console.warn(`[SignalR] ${message}`);
  },
};

export function createHubConnection(
  url: string,
  getAccessToken: () => Promise<string> | string,
): HubConnection {
  const connection = new HubConnectionBuilder()
    .withUrl(`${BASE_URL}${url}`, {
      // SignalR's accessTokenFactory accepts a sync string or a Promise.
      accessTokenFactory: () => Promise.resolve(getAccessToken()),
      // Auth is via Bearer JWT (above) — no cookies involved. SignalR's
      // browser default is `credentials: 'include'` which forces a CORS
      // preflight that requires `Access-Control-Allow-Credentials: true`
      // on the response. Backend's CORS policy doesn't (and shouldn't)
      // set that, so the preflight 200s with empty ACAC and the browser
      // blocks the actual negotiate request. Setting this to `false`
      // matches the `apiClient` pattern (native fetch with no
      // credentials option → default `same-origin` → no cookies sent
      // cross-origin) and lets the preflight pass with just the
      // origin allowlist that ASP.NET Core CORS already returns.
      withCredentials: false,
    })
    .withAutomaticReconnect()
    .configureLogging(QUIET_LOGGER)
    .build();

  // Sane defaults — see the comment block above for why we don't copy
  // Flutter's absurd values. The lib pings the server every 15 s, and
  // tolerates 60 s of server silence before declaring the connection dead.
  connection.serverTimeoutInMilliseconds = 60_000;
  connection.keepAliveIntervalInMilliseconds = 15_000;

  return connection;
}
